import { useEffect, useState } from 'react';
import type { ClientListItem, ClientRefInput } from '@/api/base-types';
import { useClientCheck, useClientPick } from '@/api/base';
import { ChoiceGroup } from '@/components/kit/Controls';
import { DateInput } from '@/components/kit/DateInput';
import { PhoneField, TextField } from '@/components/kit/Field';
import { Note } from '@/components/kit/Note';
import { FieldHint, OptionList } from '@/components/kit/OptionList';
import { TextLink } from '@/components/kit/Controls';
import { formatPhone, years } from '@/lib/phone';
import { rolesLine } from './labels';

/**
 * Выбор клиента в диалогах — один компонент на все формы (ТЗ M2 §8,
 * clPicker в макете): «Найти в базе» по ФИО или телефону, до пяти
 * совпадений, или «Новый клиент». Поле, которого не хватает для роли,
 * просится тут же: телефон — руководителю, контакту и представителю,
 * дата рождения — участнику (БЛ 5.2).
 */

export type Need = 'phone' | 'birth' | null;
type Mode = 'later' | 'find' | 'new';

export interface PickerState {
  mode: Mode;
  q: string;
  chosen: ClientListItem | null;
  lastName: string;
  firstName: string;
  phone: string;
  birthDate: string | null;
  birthValid: boolean;
}

export const emptyPicker = (later = false): PickerState => ({
  mode: later ? 'later' : 'find',
  q: '',
  chosen: null,
  lastName: '',
  firstName: '',
  phone: '',
  birthDate: null,
  birthValid: true,
});

/** Пропустить можно, только если форма разрешает «Позже» */
export function pickerReady(s: PickerState, need: Need): boolean {
  if (s.mode === 'later') return true;
  const phoneOk = s.phone.trim() !== '';
  const birthOk = Boolean(s.birthDate) && s.birthValid;
  if (s.mode === 'find') {
    if (!s.chosen) return false;
    if (need === 'phone' && !s.chosen.phone) return phoneOk;
    if (need === 'birth' && !s.chosen.birthDate) return birthOk;
    return true;
  }
  if (!s.lastName.trim() || !s.firstName.trim()) return false;
  if (need === 'phone') return phoneOk;
  if (need === 'birth') return birthOk;
  return true;
}

/** Тело для API: `{clientId, phone?, birthDate?}` или `{new:{…}}`; «Позже» — null */
export function pickerPayload(s: PickerState, need: Need): ClientRefInput | null {
  if (s.mode === 'later') return null;
  if (s.mode === 'find' && s.chosen) {
    return {
      clientId: s.chosen.id,
      ...(need === 'phone' && !s.chosen.phone ? { phone: s.phone } : {}),
      ...(need === 'birth' && !s.chosen.birthDate && s.birthDate ? { birthDate: s.birthDate } : {}),
    };
  }
  return {
    new: {
      lastName: s.lastName.trim(),
      firstName: s.firstName.trim(),
      // телефон и необязательным полем (withPhone) уходит, если заполнен
      ...(need === 'phone' || s.phone.trim() ? { phone: s.phone } : {}),
      ...(need === 'birth' ? { birthDate: s.birthDate } : {}),
    },
  };
}

/** Подпись строки найденного: «10 лет · +7 914 … · участник» */
export function clientDescription(c: ClientListItem): string {
  const age = c.birthDate ? ageFrom(c.birthDate) : null;
  return [
    age !== null ? years(age) : '',
    c.phone ? formatPhone(c.phone) : 'без телефона',
    c.roles.length ? rolesLine(c.roles, c.studioRoles).toLowerCase() : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

function ageFrom(iso: string): number {
  const b = new Date(`${iso}T00:00:00`);
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a -= 1;
  return a;
}

export function ClientPicker({
  label,
  need,
  value,
  onChange,
  exclude = [],
  allowLater,
  withPhone,
}: {
  label: string;
  need: Need;
  value: PickerState;
  onChange: (next: PickerState) => void;
  exclude?: string[];
  allowLater?: boolean;
  /** У нового клиента — ещё и телефон, необязательный (участник из номера, Road Map п. 18) */
  withPhone?: boolean;
}) {
  const set = (patch: Partial<PickerState>) => onChange({ ...value, ...patch });
  const [q, setQ] = useState(value.q);
  useEffect(() => {
    const t = window.setTimeout(() => q !== value.q && set({ q }), 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const found = useClientPick(value.mode === 'find' ? value.q : '', exclude);
  const phoneToCheck =
    ((value.mode === 'new' || (value.mode === 'find' && value.chosen && !value.chosen.phone)) && need === 'phone') ||
    (value.mode === 'new' && withPhone)
      ? value.phone
      : '';
  const check = useClientCheck({
    phone: phoneToCheck,
    ...(value.mode === 'new' && need === 'birth'
      ? { lastName: value.lastName, firstName: value.firstName, birthDate: value.birthDate }
      : {}),
    exclude: value.chosen?.id,
  });
  const phoneError = phoneToCheck.trim()
    ? check.data?.phoneInvalid
      ? 'Неполный номер'
      : check.data?.phoneTaken
        ? `Номер уже у клиента «${check.data.phoneTaken.fullName}» — выберите его через поиск`
        : null
    : null;
  const twin = value.mode === 'new' ? check.data?.twins.find((t) => !t.archived) : undefined;

  const modes: { value: Mode; label: string }[] = [
    ...(allowLater ? [{ value: 'later' as Mode, label: 'Позже' }] : []),
    { value: 'find', label: 'Найти в базе' },
    { value: 'new', label: 'Новый клиент' },
  ];
  const results = found.data ?? [];

  return (
    <>
      <div className="field">
        <span className="lbl">{label}</span>
        <div>
          <ChoiceGroup items={modes} value={value.mode} onChange={(mode) => set({ mode })} />
        </div>
      </div>

      {value.mode === 'find' ? (
        <>
          <TextField label="Поиск" placeholder="ФИО или телефон" value={q} onValue={setQ} autoFocus />
          {value.q.trim().length < 2 ? (
            <FieldHint gap>Начните вводить — покажем совпадения.</FieldHint>
          ) : results.length ? (
            <OptionList
              value={value.chosen?.id ?? null}
              onChange={(id) => set({ chosen: results.find((r) => r.id === id) ?? null, phone: '', birthDate: null })}
              options={results.map((c) => ({ value: c.id, title: c.fullName, description: clientDescription(c) }))}
            />
          ) : found.isFetching ? null : (
            <FieldHint gap>
              Никого не нашли. <TextLink onClick={() => set({ mode: 'new' })}>Завести нового</TextLink>
            </FieldHint>
          )}
          {value.chosen && need === 'phone' && !value.chosen.phone ? (
            <PhoneField
              label="Телефон — нужен для этой роли"
              value={value.phone}
              onValue={(phone) => set({ phone })}
              error={phoneError}
            />
          ) : null}
          {value.chosen && need === 'birth' && !value.chosen.birthDate ? (
            <DateInput
              label="Дата рождения — нужна участнику"
              value={value.birthDate}
              onChange={(birthDate, birthValid) => set({ birthDate, birthValid })}
            />
          ) : null}
        </>
      ) : null}

      {value.mode === 'new' ? (
        <>
          <div className="row keep">
            <div className="col">
              <TextField label="Фамилия" value={value.lastName} onValue={(lastName) => set({ lastName })} autoFocus />
            </div>
            <div className="col">
              <TextField label="Имя" value={value.firstName} onValue={(firstName) => set({ firstName })} />
            </div>
          </div>
          {need === 'birth' ? (
            <DateInput
              label="Дата рождения"
              value={value.birthDate}
              onChange={(birthDate, birthValid) => set({ birthDate, birthValid })}
            />
          ) : null}
          {need === 'phone' || withPhone ? (
            <PhoneField
              label={need === 'phone' ? 'Телефон' : 'Телефон, если есть'}
              value={value.phone}
              onValue={(phone) => set({ phone })}
              error={phoneError}
            />
          ) : null}
          {twin ? (
            <div className="mnote">
              <Note tone="warn">
                <b>Похоже на дубль:</b> уже есть «{twin.fullName}» с той же датой рождения.{' '}
                <TextLink
                  onClick={() => {
                    setQ(twin.fullName);
                    onChange({ ...value, mode: 'find', q: twin.fullName, chosen: null });
                  }}
                >
                  Выбрать его
                </TextLink>
              </Note>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}
